import * as THREE from 'three';
import { emis, std } from '../materials.ts';
import { M, V, glowSprite, limb } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * El Rondador (docs/LABERINTOS.md): the thing that prowls the Furrows and never falls. A tall, stooped shape grown
 * out of the shell itself: plates of dark slate for a back, moss and roots in the joints, arms so long the knuckles
 * scrape the floor, and a long blind head with two small red slits for eyes. Cracks of dragon red run where the
 * plates meet; when it is struck they flare (the `mend*` bones, the troll's rig, so it moves with the troll's clips).
 */

const UP = V(0, 1, 0);
/** Its light: the cracks and the eyes. */
export const PROWLER_RED = '#ff4f3a';

export const prowler = (o?: BaseOpts): Model => {
  const c = begin(o, 0.9, 61);
  const { g, R } = c;
  const slate = std('#2c3036', { roughness: 0.7, metalness: 0.05, flatShading: true, bumpScale: 0.07 });
  const slateD = std('#1c1f24', { roughness: 0.8, flatShading: true, bumpScale: 0.08 });
  const slateL = std('#40454d', { roughness: 0.65, flatShading: true, bumpScale: 0.06 });
  const moss = std('#26331c', { roughness: 0.95, flatShading: true, side: THREE.DoubleSide });
  const root = std('#2a2119', { roughness: 0.9, flatShading: true });
  const claw = std('#0e0f12', { roughness: 0.3, metalness: 0.2 });
  const crack = emis('#ffb3a3', PROWLER_RED, 1.6);

  // The troll's skeleton: the same bones in the same places, so its clips fit.
  const SH = [-1, 1].map((k) => V(k * 0.44, 1.8, 0.28));
  const EL = [-1, 1].map((k) => V(k * 0.66, 1.18, 0.38));
  const WR = [-1, 1].map((k) => V(k * 0.64, 0.5, 0.52));
  const hipsJ = joint(g, V(0, 1.02, -0.06), 'hips');
  const chestJ = joint(hipsJ.abs, V(0, 1.18, -0.04), 'chest');
  const ch = chestJ.abs;
  const headJ = joint(ch, V(0, 1.9, 0.52), 'head');
  const jawJ = joint(headJ.abs, V(0, 1.82, 0.72), 'jaw');
  const maneJ = joint(ch, V(0, 2.06, 0.2), 'mane');
  const armJ = [-1, 1].map((k, i) => joint(ch, SH[i]!, k < 0 ? 'armL' : 'armR'));
  const foreJ = [-1, 1].map((k, i) => joint(armJ[i]!.abs, EL[i]!, k < 0 ? 'foreL' : 'foreR'));
  const handJ = [-1, 1].map((k, i) => joint(foreJ[i]!.abs, WR[i]!, k < 0 ? 'handL' : 'handR'));
  const legJ = [-1, 1].map((k) => joint(hipsJ.abs, V(k * 0.22, 1.0, -0.08), k < 0 ? 'legL' : 'legR'));
  const KNEE = [-1, 1].map((k) => V(k * 0.32, 0.56, 0.14));
  const shinJ = [-1, 1].map((k, i) => joint(legJ[i]!.abs, KNEE[i]!, k < 0 ? 'shinL' : 'shinR'));

  /** A slab of shell: a flattened, faceted stone. */
  const plate = (parent: THREE.Object3D, mat: THREE.Material, r: number, at: THREE.Vector3, sx: number, sy: number, sz: number, rot: THREE.Euler): THREE.Mesh => {
    const m = M(new THREE.DodecahedronGeometry(r, 0), mat, parent);
    m.position.copy(at);
    m.scale.set(sx, sy, sz);
    m.rotation.copy(rot);
    return m;
  };
  /** A red crack between plates, from a to b, on its own bone (scaled to flare). */
  const seam = (bone: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3): void => {
    const pts = [a, a.clone().lerp(b, 0.35).add(V(R() * 0.05, R() * 0.05, 0.02)), a.clone().lerp(b, 0.7).add(V(R() * 0.05, R() * 0.05, 0.02)), b];
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 6, 0.018, 3), crack, bone);
    glowSprite(bone, PROWLER_RED, 0.32, a.clone().lerp(b, 0.5).add(V(0, 0, 0.04)), 0.4);
  };

  // Legs: thick at the thigh, plated at the knee, on wide three-clawed feet.
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const th = legJ[i]!.abs;
    const sh = shinJ[i]!.abs;
    const hip = V(k * 0.22, 1.0, -0.08);
    const knee = KNEE[i]!;
    const ank = V(k * 0.28, 0.12, -0.1);
    limb(th, hip, knee, 0.17, 0.12, slate, 7);
    plate(th, slateL, 0.16, V(k * 0.3, 0.6, 0.2), 1, 0.8, 0.55, new THREE.Euler(0.3, k * 0.4, 0));
    limb(sh, knee, ank, 0.11, 0.08, slate, 6);
    plate(sh, slateD, 0.13, V(k * 0.3, 0.06, 0.08), 1.1, 0.45, 1.7, new THREE.Euler(0, k * 0.2, 0));
    for (let f = 0; f < 3; f++) {
      const a = k * 0.2 + (f - 1) * 0.4;
      const cl = M(new THREE.ConeGeometry(0.025, 0.12, 4), claw, sh);
      cl.position.set(k * 0.3 + Math.sin(a) * 0.14, 0.03, 0.26 + Math.cos(a) * 0.06);
      cl.rotation.x = Math.PI / 2;
    }
  }
  const mendLJ = joint(legJ[1]!.abs, V(0.28, 0.8, 0.1), 'mendL');
  seam(mendLJ.abs, V(0.24, 0.92, 0.12), V(0.32, 0.66, 0.16));

  // Hips and a hanging fringe of moss and root.
  const hp = hipsJ.abs;
  plate(hp, slateD, 0.26, V(0, 1.0, -0.04), 1.3, 0.75, 1, new THREE.Euler(0, 0.3, 0));
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    limb(hp, V(Math.cos(a) * 0.26, 0.92, Math.sin(a) * 0.22), V(Math.cos(a) * 0.3, 0.62 + R() * 0.1, Math.sin(a) * 0.26), 0.03, 0.012, i % 2 ? moss : root, 4);
  }

  // Torso: pitched far forward, a ridge of shell plates down the back, moss in the gaps.
  plate(ch, slate, 0.36, V(0, 1.55, 0.16), 1.05, 1.2, 0.85, new THREE.Euler(0.85, 0, 0));
  for (let i = 0; i < 5; i++) {
    const y = 2.02 - i * 0.2;
    const z = -0.04 - i * 0.07;
    plate(ch, i % 2 ? slateL : slate, 0.2 - i * 0.015, V(R() * 0.06, y, z), 1.6, 0.5, 1.1, new THREE.Euler(0.7 + R() * 0.3, R() * 0.4, R() * 0.3));
    plate(ch, moss, 0.09, V(R() * 0.2, y - 0.08, z - 0.05), 1.5, 0.4, 1.2, new THREE.Euler(R(), R(), R()));
  }
  const mendJ = joint(ch, V(0, 1.55, 0.42), 'mend');
  seam(mendJ.abs, V(-0.18, 1.72, 0.4), V(0.14, 1.38, 0.46));

  // The mane: a mantle of moss and hanging roots over the shoulders.
  const mn = maneJ.abs;
  for (let i = 0; i < 9; i++) {
    const a = ((i / 8) - 0.5) * 2.6;
    limb(mn, V(Math.sin(a) * 0.4, 2.04, 0.2 + Math.cos(a) * 0.12), V(Math.sin(a) * 0.48, 1.66 + R() * 0.12, 0.12 + Math.cos(a) * 0.18), 0.045, 0.015, i % 3 ? moss : root, 4);
  }

  // The head: long, eyeless but for two red slits, plated brow, a hinged jaw full of stone teeth.
  const hd = headJ.abs;
  plate(hd, slate, 0.2, V(0, 1.92, 0.66), 0.9, 0.75, 1.6, new THREE.Euler(0.25, 0, 0));
  plate(hd, slateL, 0.13, V(0, 2.02, 0.62), 1.4, 0.4, 1.4, new THREE.Euler(0.2, 0, 0));
  for (const k of [-1, 1]) {
    const eye = M(new THREE.BoxGeometry(0.07, 0.016, 0.02), crack, hd);
    eye.position.set(k * 0.07, 1.95, 0.92);
    eye.rotation.z = k * 0.25;
    glowSprite(hd, PROWLER_RED, 0.16, V(k * 0.07, 1.95, 0.95), 0.7);
  }
  const jw = jawJ.abs;
  plate(jw, slateD, 0.14, V(0, 1.78, 0.82), 0.8, 0.45, 1.4, new THREE.Euler(0.2, 0, 0));
  for (let i = 0; i < 4; i++) {
    const t = M(new THREE.ConeGeometry(0.014, 0.05, 3), slateL, jw);
    t.position.set(-0.06 + i * 0.04, 1.84, 0.96);
  }

  // Arms: long and thick with plates on the shoulders; huge hands that drag, black stone claws.
  for (const k of [-1, 1]) {
    const i = k < 0 ? 0 : 1;
    const up = armJ[i]!.abs;
    const fo = foreJ[i]!.abs;
    const hn = handJ[i]!.abs;
    plate(up, slateL, 0.2, SH[i]!.clone().add(V(k * 0.04, 0.08, -0.02)), 1.2, 0.6, 1.1, new THREE.Euler(R() * 0.4, 0, k * 0.4));
    limb(up, SH[i]!, EL[i]!, 0.12, 0.09, slate, 6);
    limb(fo, EL[i]!, WR[i]!, 0.09, 0.11, slate, 6);
    plate(fo, slateD, 0.12, V(k * 0.68, 0.88, 0.44), 0.8, 1.5, 0.8, new THREE.Euler(0, 0, k * 0.1));
    plate(hn, slateD, 0.13, V(k * 0.64, 0.4, 0.56), 1, 1.2, 0.7, new THREE.Euler(0.2, 0, 0));
    for (let f = 0; f < 4; f++) {
      const x = k * (0.64 + (f - 1.5) * 0.06);
      limb(hn, V(x, 0.34, 0.6), V(x, 0.1, 0.68), 0.028, 0.02, slate, 4);
      const cl = M(new THREE.ConeGeometry(0.02, 0.12, 4), claw, hn);
      cl.position.set(x, 0.04, 0.72);
      cl.quaternion.setFromUnitVectors(UP, V(0, -0.4, 1).normalize());
    }
  }
  const mendAJ = joint(armJ[0]!.abs, V(-0.56, 1.46, 0.36), 'mendA');
  seam(mendAJ.abs, V(-0.5, 1.62, 0.34), V(-0.62, 1.3, 0.42));

  // A low red breath in the chest.
  const heart = glowSprite(ch, PROWLER_RED, 1.0, V(0, 1.5, 0.36), 0.1, true);
  const t = (n: number): void => {
    heart.material.opacity = 0.08 + ((n * 7) % 11) / 110;
  };
  t(0);
  return done(c, 2.3, t, undefined, bonesOf(c, {
    hips: hipsJ.p, chest: chestJ.p, head: headJ.p, jaw: jawJ.p, mane: maneJ.p,
    armL: armJ[0]!.p, foreL: foreJ[0]!.p, handL: handJ[0]!.p, armR: armJ[1]!.p, foreR: foreJ[1]!.p, handR: handJ[1]!.p,
    legL: legJ[0]!.p, shinL: shinJ[0]!.p, legR: legJ[1]!.p, shinR: shinJ[1]!.p,
    mend: mendJ.p, mendA: mendAJ.p, mendL: mendLJ.p,
  }));
};
